import { AppCheckToken, CustomProviderOptions } from 'firebase/app-check';

const TURNSTILE_API_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=onloadTurnstileCallback';
const TURNSTILE_DIV_ID = 'turnstile-widget';
const TURNSTILE_CLASS_NAME = 'cf-turnstile';

interface TurnstileWindow extends Window {
  turnstile: {
    render: (container: string | HTMLElement, options: {
      sitekey: string;
      callback?: (token: string) => void;
      'error-callback'?: () => void;
      'expired-callback'?: () => void;
    }) => string;
    reset: (widgetId?: string) => void;
    getResponse: (widgetId?: string) => string | null;
  };
  onloadTurnstileCallback?: () => void;
}

declare const window: TurnstileWindow;

let promiseResolve: (value: unknown) => void;
let isPromiseResolved = false;
const readyTurnstile = new Promise(resolve => {
  promiseResolve = resolve;
});

let cachedToken: AppCheckToken | null = null;
let tokenExpireTimeMillis = 0;

/**
 * CloudflareProviderOptions implementation that follows the official
 * Cloudflare Turnstile Firebase documentation exactly.
 * This is a workaround for Create React App compilation issues.
 * 
 * Reference: https://developers.cloudflare.com/turnstile/extensions/google-firebase/
 */
class CloudflareProviderOptions implements CustomProviderOptions {
  private _tokenExchangeUrl: string;
  private _siteKey: string;
  private _isInitialized = false;

  constructor(tokenExchangeUrl: string, siteKey: string) {
    this._tokenExchangeUrl = tokenExchangeUrl;
    this._siteKey = siteKey;
    this.initialize();
  }

  private initialize() {
    if (this._isInitialized) return;
    
    try {
      const body = document.body;
      const turnstileElement = this.createTurnstileDiv();
      body.appendChild(turnstileElement);
      
      // Only add script if not already present
      if (!document.querySelector(`script[src*="challenges.cloudflare.com/turnstile"]`)) {
        body.appendChild(this.createTurnstileScript());
      }

      // Set up callback and handle case where script is already loaded
      const setupTurnstileWidget = () => {
        try {
          if (window.turnstile && typeof window.turnstile.render === 'function') {
            const widgetId = window.turnstile.render(turnstileElement, {
              sitekey: this._siteKey,
              callback: (token: string) => {
                // Token received - handled by getResponse
              },
              'error-callback': (errorCode?: string) => {
                console.error('Turnstile error:', errorCode);
              },
              'expired-callback': () => {
                // Token expired - will be regenerated when needed
              }
            });
            
            // Resolve promise when widget is successfully rendered
            if (!isPromiseResolved) {
              promiseResolve(true);
              isPromiseResolved = true;
            }
          } else {
            if (!isPromiseResolved) {
              promiseResolve(false);
              isPromiseResolved = true;
            }
          }
        } catch (error) {
          console.error('Error initializing Turnstile widget:', error);
          if (!isPromiseResolved) {
            promiseResolve(false);
            isPromiseResolved = true;
          }
        }
      };

      // Set up callback for future loads
      window.onloadTurnstileCallback = setupTurnstileWidget;

      // If Turnstile is already available, render immediately
      if (window.turnstile && typeof window.turnstile.render === 'function') {
        setupTurnstileWidget();
      } else {
        // Add timeout in case Turnstile never loads
        setTimeout(() => {
          if (!isPromiseResolved) {
            console.error('Turnstile script load timeout after 10 seconds');
            promiseResolve(false);
            isPromiseResolved = true;
          }
        }, 10000);
      }

      this._isInitialized = true;
    } catch (error) {
      console.error('Error setting up Turnstile provider:', error);
      throw error;
    }
  }

  private createTurnstileDiv(): HTMLElement {
    const div = document.createElement('div');
    div.id = TURNSTILE_DIV_ID;
    div.className = TURNSTILE_CLASS_NAME;
    div.style.display = 'none';
    return div;
  }

  private createTurnstileScript(): HTMLElement {
    const script = document.createElement('script');
    script.src = TURNSTILE_API_URL;
    script.async = true;
    script.defer = true;
    return script;
  }

  getSiteKey(): string {
    return this._siteKey;
  }

  async getToken(): Promise<{
    readonly token: string;
    readonly expireTimeMillis: number;
  }> {
    return this.renderAndExchange(false);
  }

  async getLimitedUseToken(): Promise<{
    readonly token: string;
    readonly expireTimeMillis: number;
  }> {
    return this.renderAndExchange(true);
  }

  private async renderAndExchange(limitedUse: boolean): Promise<AppCheckToken> {
    try {
      // Return cached token if still valid
      if (cachedToken !== null && tokenExpireTimeMillis > Date.now()) {
        return cachedToken;
      }

      // Wait for Turnstile to be ready
      await readyTurnstile;

      // Get Turnstile token
      const turnstileToken = window.turnstile?.getResponse(`#${TURNSTILE_DIV_ID}`);
      
      if (!turnstileToken) {
        throw new Error('Failed to get Turnstile token - please refresh and try again');
      }

      // Exchange token with Firebase function
      const response = await fetch(this._tokenExchangeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          turnstileToken,
          limitedUse,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Token exchange failed: ${response.status} ${response.statusText}. Please check your Firebase extension configuration.`);
      }

      const appCheckToken: AppCheckToken = await response.json();
      
      if (!appCheckToken.token) {
        throw new Error('Invalid App Check token received from Firebase function');
      }

      // Cache the token
      cachedToken = appCheckToken;
      tokenExpireTimeMillis = Date.now() + (55 * 60 * 1000); // 55 minutes (5min buffer)

      // Reset Turnstile widget for next use
      try {
        if (window.turnstile?.reset) {
          window.turnstile.reset(TURNSTILE_DIV_ID);
        }
      } catch (resetError) {
        // Ignore reset errors - widget might already be in correct state
      }

      return appCheckToken;
    } catch (error) {
      console.error('Error in renderAndExchange:', error);
      throw error;
    }
  }
}

// Factory function to create the provider (maintains compatibility)
export const createTurnstileProvider = (tokenExchangeUrl: string, siteKey: string): CustomProviderOptions => {
  return new CloudflareProviderOptions(tokenExchangeUrl, siteKey);
};
